import { Component } from "@angular/core";
import { Router } from "@angular/router";
import { BlockStatusEnum } from "@enums/blockStatus.enum";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { ProductionToolService } from "@services/productionTool.service";
import { ToolOrderService } from "@services/toolOrder.service";
import { ToastService } from "angular-toastify";
import { ToastApiError } from "@helpers/apiError";

@Component({
	moduleId: module.id,
	selector: "blockBoard",
	styleUrls: ["blockBoard.css"],
	templateUrl: "blockBoard.html"
})
export class BlockBoardAdminComponent {
	blocks: any[] = [];
	columns: any[] = [];
	dragOverStatus: number | null = null;
	draggingBlockId: number | null = null;
	draggingToolId: number | null = null;
	draggingType: string | null = null;
	dueSoonCount = 0;
	headerPrimaryButtons: AdminHeaderButton[] = [{ icon: "fal fa-sync-alt", method: "getData", text: "Refresh" }];
	isLoading = false;
	overdueCount = 0;
	// One icon per status, used on the column header and its pill at the top.
	statusIcons: { [status: number]: string } = {
		[BlockStatusEnum.Designing]: "fa-pencil-ruler",
		[BlockStatusEnum.Designed]: "fa-drafting-compass",
		[BlockStatusEnum.Programming]: "fa-laptop-code",
		[BlockStatusEnum.Programmed]: "fa-file-code",
		[BlockStatusEnum.Machining]: "fa-cogs",
		[BlockStatusEnum.Machined]: "fa-cube",
		[BlockStatusEnum.Assembling]: "fa-tools",
		[BlockStatusEnum.Assembled]: "fa-puzzle-piece",
		[BlockStatusEnum.Done]: "fa-check-circle",
	};
	statuses: any[] = [];
	title = "Operations Board";
	// Tools with no billets on them at all: nothing has been broken down to design yet.
	toolsToDesign: any[] = [];

	constructor(private router: Router, private toastService: ToastService, private itemService: ToolOrderService, private productionToolService: ProductionToolService) {
		this.statuses = GetPropertiesFromEnum(BlockStatusEnum);
		this.getData();
	}

	// Group the blocks in each status column by their tool, so a tool card only shows the blocks it has
	// in that status. A tool spread across statuses therefore appears (duplicated) in each column it touches.
	// What each card shows is worked out here once, rather than by template calls on every change detection.
	buildColumns() {
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const weekAhead = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
		this.overdueCount = 0;
		this.dueSoonCount = 0;

		this.blocks.forEach((b) => {
			const operations: any[] = b.operations || [];
			const done = operations.filter((o) => o.stageComplete).length;
			b.progress = operations.length > 0 ? { done, percent: (done / operations.length) * 100, total: operations.length } : null;

			// Done billets aren't late.
			const due = b.dateRequired ? new Date(b.dateRequired) : null;
			b.dueState = !due || (b.status || 0) === BlockStatusEnum.Done ? "" : due < today ? "overdue" : due <= weekAhead ? "soon" : "";
			if (b.dueState === "overdue") this.overdueCount++;
			if (b.dueState === "soon") this.dueSoonCount++;
		});

		this.columns = this.statuses.map((s: any) => {
			const inStatus = this.blocks.filter((b) => (b.status || 0) === s.id);
			const toolMap: any = {};
			inStatus.forEach((b) => {
				const toolId = b.productionToolId;
				if (!toolMap[toolId]) {
					toolMap[toolId] = { blocks: [], imageUrl: this.firstImage(b.productionTool?.imageURLs), toolId, toolName: b.productionTool ? b.productionTool.name : "Tool " + toolId };
				}
				toolMap[toolId].blocks.push(b);
			});
			const tools = Object.values(toolMap).sort((a: any, b: any) => a.toolName.localeCompare(b.toolName));
			return { count: inStatus.length, icon: this.statusIcons[s.id] || "fa-circle", key: s.name.toLowerCase(), status: s.id, statusName: s.name, tools };
		});
	}

	// A tool's images are stored as JSON; the board shows the first.
	firstImage(imageUrls: string): string {
		try {
			const images = JSON.parse(imageUrls || "[]");
			return (Array.isArray(images) ? images : []).find((i: any) => i?.fileUrl)?.fileUrl || "";
		} catch (e) {
			return "";
		}
	}

	getData() {
		this.isLoading = true;
		this.itemService.getForBoard()
			.subscribe(
				(res: any) => {
					this.blocks = res.response || res || [];
					this.buildColumns();
					// Adding or removing a billet changes what is still to be designed, so the two load together.
					this.getToolsToDesign();
					this.isLoading = false;
				},
				(err) => { console.log(err); this.isLoading = false; ToastApiError(this.toastService, err, "Could not load the board."); }
			);
	}

	// A tool with no billets never appears in a status column, so the board would not show it at all.
	getToolsToDesign() {
		this.productionToolService.withNoBillets()
			.subscribe(
				(res: any) => {
					const today = new Date();
					today.setHours(0, 0, 0, 0);
					const weekAhead = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

					this.toolsToDesign = (res.response || res || []).map((tool: any) => {
						const due = tool.dateRequired ? new Date(tool.dateRequired) : null;
						return {
							clientName: tool.client ? tool.client.name : "",
							dateRequired: tool.dateRequired,
							dueState: due == null ? "" : due < today ? "overdue" : due <= weekAhead ? "soon" : "",
							imageUrl: this.firstImage(tool.imageURLs),
							toolId: tool.id,
							toolName: tool.name || ("Tool " + tool.id)
						};
					});
				},
				(err) => { console.log(err); ToastApiError(this.toastService, err, "Could not load the tools still to be designed."); }
			);
	}

	goToBlock(id: number) {
		this.router.navigate([`/admin/toolOrders/${id}`]);
	}

	goToTool(id: number) {
		this.router.navigate([`/admin/productionTools/${id}`]);
	}

	headerButtonClick(method: any) {
		(this as any)[method]();
	}

	onBlockDragStart(event: DragEvent, block: any) {
		this.draggingType = "block";
		this.draggingBlockId = block.id;
		this.draggingToolId = null;
		if (event.dataTransfer) { event.dataTransfer.effectAllowed = "move"; }
	}

	onDragEnd() {
		this.draggingType = null;
		this.draggingBlockId = null;
		this.draggingToolId = null;
		this.dragOverStatus = null;
	}

	onDragLeave(status: number) {
		if (this.dragOverStatus === status) { this.dragOverStatus = null; }
	}

	onDragOver(event: DragEvent, status: number) {
		event.preventDefault();
		this.dragOverStatus = status;
	}

	onDrop(status: number) {
		const type = this.draggingType;
		const blockId = this.draggingBlockId;
		const toolId = this.draggingToolId;
		this.onDragEnd();

		if (type === "block" && blockId != null) {
			this.itemService.updateStatus(blockId, status)
				.subscribe(() => this.getData(), (err) => { console.log(err); ToastApiError(this.toastService, err, "Could not move the block."); });
		} else if (type === "tool" && toolId != null) {
			this.itemService.updateToolStatus(toolId, status)
				.subscribe(() => this.getData(), (err) => { console.log(err); ToastApiError(this.toastService, err, "Could not move the tool."); });
		}
	}

	onToolDragStart(event: DragEvent, toolId: number) {
		this.draggingType = "tool";
		this.draggingToolId = toolId;
		this.draggingBlockId = null;
		if (event.dataTransfer) { event.dataTransfer.effectAllowed = "move"; }
	}
}
